import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { getProjectImageUrl } from '../config';
import { compressImage } from '../utils/compressImage';
import { ShoppingCart, Monitor, Wrench, Wheat, Home, Truck, UtensilsCrossed, Shirt, Pill, BookOpen, DollarSign, FolderOpen, Rocket, Pencil, Lightbulb, FileText, ClipboardList, MapPin, Users, Check, Camera, Globe, Smartphone, Handshake, Trash2, Plus, AlertTriangle, GraduationCap, Briefcase } from 'lucide-react';

const CATEGORIES = [
  { value: 'commerce', label: 'Commerce / E-commerce', icon: ShoppingCart },
  { value: 'tech', label: 'Tech / Startup', icon: Monitor },
  { value: 'services', label: 'Services', icon: Wrench },
  { value: 'agriculture', label: 'Agriculture / Agrobusiness', icon: Wheat },
  { value: 'immobilier', label: 'Immobilier / BTP', icon: Home },
  { value: 'transport', label: 'Transport / Logistique', icon: Truck },
  { value: 'restauration', label: 'Restauration / Food', icon: UtensilsCrossed },
  { value: 'mode', label: 'Mode / Textile', icon: Shirt },
  { value: 'sante', label: 'Santé / Bien-être', icon: Pill },
  { value: 'education', label: 'Éducation / Formation', icon: BookOpen },
  { value: 'finance', label: 'Finance / Assurance', icon: DollarSign },
  { value: 'autre', label: 'Autre', icon: FolderOpen }
];

const PAYS_AFRIQUE = [
  'Sénégal', 'Côte d\'Ivoire', 'Mali', 'Burkina Faso', 'Cameroun', 'Guinée',
  'Bénin', 'Togo', 'Niger', 'Congo', 'Gabon', 'RDC', 'Maroc', 'Algérie',
  'Tunisie', 'Nigeria', 'Ghana', 'Kenya', 'Afrique du Sud', 'Autre'
];

const CreateProject = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  
  // Étape actuelle du formulaire
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // État du formulaire principal
  const [form, setForm] = useState({
    titre: '',
    description_courte: '',
    description: '',
    categorie: '',
    budget: '',
    deadline: '',
    visibilite: 'public',
    type_projet: 'long_terme',
    lieu_ville: '',
    lieu_pays: '',
    niveau_requis: 'tous',
    lien_site: '',
    lien_social: '',
    tags: []
  });
  
  // Gestion des tags
  const [tagInput, setTagInput] = useState('');
  
  // Image de couverture
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  
  // Tâches
  const [tasks, setTasks] = useState([]);
  const [existingTasks, setExistingTasks] = useState([]);
  const [newTask, setNewTask] = useState({ 
    titre: '', 
    description: '', 
    type: 'collaboration',
    remuneration: '',
    competences_requises: '',
    valeur_parts: ''
  });
  
  // États UI
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Charger les données en mode édition
  useEffect(() => {
    if (isEditMode) {
      loadProjectData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProjectData = async () => {
    setLoadingData(true);
    try {
      const res = await api.get(`/projects/${id}`);
      const project = res.data;
      
      setForm({
        titre: project.titre || '',
        description_courte: project.description_courte || '',
        description: project.description || '',
        categorie: project.categorie || '',
        budget: project.budget || '',
        deadline: project.deadline ? project.deadline.split('T')[0] : '',
        visibilite: project.visibilite || 'public',
        type_projet: project.type_projet || 'ponctuel',
        lieu_ville: project.lieu_ville || '',
        lieu_pays: project.lieu_pays || '',
        niveau_requis: project.niveau_requis || 'tous',
        lien_site: project.lien_site || '',
        lien_social: project.lien_social || '',
        tags: project.tags ? (typeof project.tags === 'string' ? JSON.parse(project.tags) : project.tags) : []
      });
      
      if (project.image_couverture) {
        setCoverPreview(getProjectImageUrl(project.image_couverture));
      }
      
      if (project.tasks && Array.isArray(project.tasks)) {
        setExistingTasks(project.tasks);
      }
    } catch (err) {
      console.error('Erreur chargement projet:', err);
      setError('Erreur lors du chargement du projet');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim()) && form.tags.length < 10) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleImageChange = async (e) => {
    let file = e.target.files[0];
    if (file) {
      // Compresser l'image avant
      try {
        file = await compressImage(file, { maxSizeMB: 1 });
      } catch (err) {
        console.error('Erreur compression:', err);
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('L\'image ne doit pas dépasser 5 Mo');
        return;
      }
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAddTask = () => {
    if (!newTask.titre) return;
    setTasks([...tasks, { ...newTask, id: Date.now() }]);
    setNewTask({ titre: '', description: '', type: 'collaboration', remuneration: '', competences_requises: '', valeur_parts: '' });
  };

  const handleRemoveTask = (taskId) => {
    setTasks(tasks.filter(t => t.id !== taskId));
  };

  const uploadCoverImage = async (projectId) => {
    if (!coverImage) return null;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', coverImage);
    formData.append('project_id', projectId);
    formData.append('type', 'cover');
    
    try {
      const res = await api.post('/projects/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data.filename;
    } catch (err) {
      console.error('Erreur upload image:', err);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const projectData = { ...form };
      
      if (isEditMode) {
        // Mode édition
        await api.put(`/projects/${id}`, projectData);
        
        // Upload nouvelle image si modifiée
        if (coverImage) {
          const imageName = await uploadCoverImage(id);
          if (imageName) {
            await api.put(`/projects/${id}`, { image_couverture: imageName });
          }
        }
        
        // Créer les nouvelles tâches
        for (const task of tasks) {
          await api.post('/tasks', {
            project_id: id,
            titre: task.titre,
            description: task.description,
            type: task.type,
            remuneration: task.remuneration || null,
            competences_requises: task.competences_requises || null,
            valeur_parts: task.valeur_parts || null
          });
        }
        
        navigate(`/project/${id}`);
      } else {
        // Mode création
        const projectRes = await api.post('/projects', projectData);
        
        if (!projectRes.data || !projectRes.data.id) {
          throw new Error(projectRes.data?.error || 'Erreur lors de la création');
        }
        
        const projectId = projectRes.data.id;
        
        // Upload image de couverture
        if (coverImage) {
          const imageName = await uploadCoverImage(projectId);
          if (imageName) {
            await api.put(`/projects/${projectId}`, { image_couverture: imageName });
          }
        }

        // Créer les tâches
        for (const task of tasks) {
          await api.post('/tasks', {
            project_id: projectId,
            titre: task.titre,
            description: task.description,
            type: task.type,
            remuneration: task.remuneration || null,
            competences_requises: task.competences_requises || null,
            valeur_parts: task.valeur_parts || null
          });
        }

        navigate(`/project/${projectId}`);
      }
    } catch (err) {
      console.error('Erreur projet:', err);
      setError(err.response?.data?.error || err.message || 'Erreur lors de l\'opération');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = (e) => {
    if (e) e.preventDefault();
    if (currentStep === 1 && (!form.titre || !form.categorie)) {
      setError('Veuillez remplir le titre et la catégorie');
      return;
    }
    setError('');
    setCurrentStep(Math.min(currentStep + 1, totalSteps));
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <div className="text-indigo-600 text-lg">Chargement du projet...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header avec explication */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-2 flex items-center justify-center gap-2">
            {isEditMode ? <><Pencil className="w-8 h-8 text-indigo-600" /> Modifier le projet</> : <><Rocket className="w-8 h-8 text-indigo-600" /> Créer votre projet</>}
          </h1>
          <p className="text-gray-600 mb-4">
            {isEditMode ? 'Mettez à jour les informations de votre projet' : 'Lancez votre entreprise et trouvez des collaborateurs talentueux'}
          </p>
          
          {/* Explication du processus */}
          {!isEditMode && (
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 max-w-2xl mx-auto text-left shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" /> Comment ça marche ?
              </h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <p className="text-gray-600"><strong>Décrivez</strong> votre projet (nom, catégorie, objectifs)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <p className="text-gray-600"><strong>Définissez</strong> les rôles à pourvoir dans votre équipe</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <p className="text-gray-600"><strong>Publiez</strong> et recevez des candidatures</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                  <p className="text-gray-600"><strong>Gérez</strong> votre équipe et vos finances</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { num: 1, label: 'Essentiel', icon: FileText },
              { num: 2, label: 'Détails', icon: ClipboardList },
              { num: 3, label: 'Localisation', icon: MapPin },
              { num: 4, label: 'Équipe', icon: Users }
            ].map((step, index) => {
              const StepIcon = step.icon;
              return (
              <React.Fragment key={step.num}>
                <div 
                  className={`flex flex-col items-center cursor-pointer transition-all ${
                    currentStep >= step.num ? 'opacity-100' : 'opacity-50'
                  }`}
                  onClick={() => step.num <= currentStep && setCurrentStep(step.num)}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl mb-1 transition-all ${
                    currentStep === step.num 
                      ? 'bg-indigo-600 text-white shadow-lg scale-110' 
                      : currentStep > step.num 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.num ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${
                    currentStep === step.num ? 'text-indigo-600' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {index < 3 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'
                  }`}></div>
                )}
              </React.Fragment>
            );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
            
            {/* Étape 1: Informations essentielles */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="bg-indigo-100 p-2 rounded-lg"><FileText className="w-5 h-5 text-indigo-600" /></span>
                    Informations essentielles
                  </h2>
                  <p className="text-gray-500 text-sm mt-1 ml-12">
                    Ces informations seront visibles par tous et aideront les talents à trouver votre projet.
                  </p>
                </div>
                
                {/* Titre */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Titre du projet <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="titre"
                    value={form.titre}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition text-lg"
                    placeholder="Ex: Boutique en ligne de produits locaux"
                  />
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Choisissez un nom accrocheur et mémorable</p>
                </div>

                {/* Description courte */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Accroche (résumé court)
                  </label>
                  <input
                    type="text"
                    name="description_courte"
                    value={form.description_courte}
                    onChange={handleChange}
                    maxLength={200}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                    placeholder="Ex: Plateforme de vente de produits artisanaux sénégalais"
                  />
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Une phrase qui résume votre projet ({form.description_courte.length}/200)</p>
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Sélectionnez le domaine principal de votre projet</p>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {CATEGORIES.map(cat => {
                      const CatIcon = cat.icon;
                      return (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setForm({ ...form, categorie: cat.value })}
                          className={`p-3 rounded-xl border-2 transition-all text-center ${
                            form.categorie === cat.value
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <span className="text-2xl block mb-1"><CatIcon className="w-6 h-6 mx-auto" /></span>
                          <span className="text-xs font-medium">{cat.label.split(' / ')[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Image de couverture */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Image de couverture
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-indigo-400 transition cursor-pointer"
                       onClick={() => document.getElementById('cover-input').click()}>
                    {coverPreview ? (
                      <div className="relative">
                        <img src={coverPreview} alt="Couverture" className="max-h-48 mx-auto rounded-lg" />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCoverImage(null); setCoverPreview(null); }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-4xl mb-2 block"><Camera className="w-10 h-10 mx-auto text-gray-400" /></span>
                        <p className="text-gray-500">Cliquez pour ajouter une image</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG (max 5 Mo)</p>
                      </>
                    )}
                    <input
                      id="cover-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Étape 2: Détails du projet */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="bg-indigo-100 p-2 rounded-lg"><ClipboardList className="w-5 h-5 text-indigo-600" /></span>
                    Détails du projet
                  </h2>
                  <p className="text-gray-500 text-sm mt-1 ml-12">
                    Plus vous donnez de détails, plus vous attirerez les bons collaborateurs.
                  </p>
                </div>

                {/* Description complète */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description complète
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition resize-none"
                    placeholder="• Quel problème résolvez-vous ?
• Quelle est votre solution ?
• Qui sont vos clients cibles ?
• Comment comptez-vous gagner de l'argent ?"
                  />
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Expliquez clairement votre vision pour convaincre les talents de vous rejoindre</p>
                </div>

                {/* Type de projet et Budget */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type de projet
                    </label>
                    <select
                      name="type_projet"
                      value={form.type_projet}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                    >
                      <option value="ponctuel">⚡ Projet ponctuel</option>
                      <option value="court_terme">📅 Lancement (3-6 mois)</option>
                      <option value="long_terme">📆 Entreprise durable</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Budget estimé (XOF)
                    </label>
                    <input
                      type="number"
                      name="budget"
                      value={form.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Deadline et Niveau */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date limite de visibilité
                    </label>
                    <input
                      type="date"
                      name="deadline"
                      value={form.deadline}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                    />
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Après cette date, votre projet ne sera plus visible dans "Pour Toi" (laissez vide si pas de limite)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Niveau d'expérience recherché
                    </label>
                    <select
                      name="niveau_requis"
                      value={form.niveau_requis}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                    >
                      <option value="tous">⭐ Tous niveaux</option>
                      <option value="debutant">🌱 Débutant</option>
                      <option value="intermediaire">📈 Intermédiaire</option>
                      <option value="expert">🏆 Expert</option>
                    </select>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mots-clés / Tags
                  </label>
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Les tags aident les talents à trouver votre projet. Ajoutez des mots liés à votre domaine, compétences recherchées, etc.</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                      placeholder="Ex: marketing, design, e-commerce, Dakar..."
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-indigo-100 text-indigo-600 rounded-xl hover:bg-indigo-200 transition font-medium"
                    >
                      + Ajouter
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-2">
                        #{tag}
                        <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">✕</button>
                      </span>
                    ))}
                  </div>
                  {form.tags.length > 0 && <p className="text-xs text-gray-400 mt-1">{form.tags.length}/10 tags</p>}
                </div>
              </div>
            )}

            {/* Étape 3: Localisation et liens */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="bg-indigo-100 p-2 rounded-lg"><MapPin className="w-5 h-5 text-indigo-600" /></span>
                    Localisation & Liens
                  </h2>
                  <p className="text-gray-500 text-sm mt-1 ml-12">
                    Optionnel mais recommandé pour attirer des collaborateurs près de vous.
                  </p>
                </div>

                {/* Localisation */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pays
                    </label>
                    <select
                      name="lieu_pays"
                      value={form.lieu_pays}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                    >
                      <option value="">-- Sélectionner --</option>
                      {PAYS_AFRIQUE.map(pays => (
                        <option key={pays} value={pays}>{pays}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ville
                    </label>
                    <input
                      type="text"
                      name="lieu_ville"
                      value={form.lieu_ville}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                      placeholder="Ex: Dakar, Abidjan..."
                    />
                  </div>
                </div>

                {/* Liens externes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Site web du projet
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><Globe className="w-4 h-4" /></span>
                    <input
                      type="url"
                      name="lien_site"
                      value={form.lien_site}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Lien réseau social (Instagram, TikTok, etc.)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><Smartphone className="w-4 h-4" /></span>
                    <input
                      type="url"
                      name="lien_social"
                      value={form.lien_social}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-blue-700 text-sm flex items-center gap-1">
                    <Lightbulb className="w-4 h-4" /> <strong>Astuce :</strong> Ajouter votre localisation et vos liens augmente la visibilité de votre projet et renforce la confiance des collaborateurs potentiels.
                  </p>
                </div>
              </div>
            )}

            {/* Étape 4: Tâches / Équipe */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="bg-indigo-100 p-2 rounded-lg"><Users className="w-5 h-5 text-indigo-600" /></span>
                    Construisez votre équipe
                  </h2>
                  <p className="text-gray-500 text-sm mt-1 ml-12">
                    Publiez des opportunités pour attirer les talents dont vous avez besoin.
                  </p>
                </div>

                {/* Explication des types */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-indigo-600">🎯</span> Quel type de collaborateur cherchez-vous ?
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="font-medium text-indigo-700 flex items-center gap-1"><Handshake className="w-4 h-4" /> Associé / Co-fondateur</p>
                      <p className="text-gray-500 text-xs">Partage les risques et les bénéfices (parts %)</p>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="font-medium text-green-700 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Prestataire / Freelance</p>
                      <p className="text-gray-500 text-xs">Mission ponctuelle contre rémunération</p>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="font-medium text-purple-700 flex items-center gap-1"><Briefcase className="w-4 h-4" /> Employé</p>
                      <p className="text-gray-500 text-xs">Salaire fixe, engagement long terme</p>
                    </div>
                  </div>
                </div>

                {/* Formulaire d'ajout de tâche */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-indigo-600" /> Ajouter une opportunité
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Titre du poste *</label>
                      <input
                        type="text"
                        placeholder="Ex: Community Manager, Développeur web..."
                        value={newTask.titre}
                        onChange={e => setNewTask({ ...newTask, titre: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type de collaboration</label>
                      <select
                        value={newTask.type}
                        onChange={e => setNewTask({ ...newTask, type: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                      >
                        <option value="collaboration">🤝 Associé / Co-fondateur (parts)</option>
                        <option value="prestation">💰 Prestataire / Freelance</option>
                        <option value="emploi">👔 Employé / CDD-CDI</option>
                        <option value="stage">🎓 Stagiaire</option>
                        <option value="partenariat">🏢 Partenaire commercial</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description des missions</label>
                    <textarea
                      placeholder="Décrivez ce que la personne devra faire, ses responsabilités..."
                      value={newTask.description}
                      onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 resize-none"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {newTask.type === 'collaboration' ? 'Bonus/Prime (XOF)' : 'Rémunération (XOF)'}
                      </label>
                      <input
                        type="number"
                        placeholder={newTask.type === 'collaboration' ? 'Optionnel' : 'Ex: 150000'}
                        value={newTask.remuneration}
                        onChange={e => setNewTask({ ...newTask, remuneration: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Parts (%) {newTask.type !== 'collaboration' && <span className="text-gray-400">- associé uniquement</span>}
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder="Ex: 10"
                        value={newTask.valeur_parts}
                        onChange={e => setNewTask({ ...newTask, valeur_parts: e.target.value })}
                        disabled={newTask.type !== 'collaboration'}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Compétences requises</label>
                      <input
                        type="text"
                        placeholder="Ex: Design, Marketing..."
                        value={newTask.competences_requises}
                        onChange={e => setNewTask({ ...newTask, competences_requises: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddTask}
                    disabled={!newTask.titre}
                    className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Ajouter cette opportunité
                  </button>
                </div>

                {/* Info si aucune tâche */}
                {tasks.length === 0 && existingTasks.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                    <p className="text-yellow-700">
                      <span className="text-2xl block mb-2"><Lightbulb className="w-8 h-8 mx-auto text-yellow-500" /></span>
                      <strong>Conseil :</strong> Ajoutez au moins une opportunité pour que votre projet apparaisse dans le fil "Pour Toi" et attire des candidats !
                    </p>
                  </div>
                )}

                {/* Liste des tâches existantes (mode édition) */}
                {isEditMode && existingTasks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Postes existants :</h3>
                    <div className="space-y-2">
                      {existingTasks.map(task => (
                        <div key={task.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">
                              {task.type === 'collaboration' ? <Handshake className="w-5 h-5 text-indigo-600" /> : task.type === 'prestation' ? <DollarSign className="w-5 h-5 text-green-600" /> : task.type === 'emploi' ? <Briefcase className="w-5 h-5 text-purple-600" /> : task.type === 'stage' ? <GraduationCap className="w-5 h-5 text-blue-600" /> : <Handshake className="w-5 h-5 text-indigo-600" />}
                            </span>
                            <div>
                              <span className="font-medium text-gray-800">{task.titre}</span>
                              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded">
                                {task.type}
                              </span>
                            </div>
                          </div>
                          <span className="text-green-600 text-sm font-medium flex items-center gap-1"><Check className="w-4 h-4" /> Existant</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Liste des nouvelles tâches */}
                {tasks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">
                      {isEditMode ? 'Nouveaux postes à ajouter :' : 'Postes définis :'}
                    </h3>
                    <div className="space-y-2">
                      {tasks.map(task => (
                        <div key={task.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">
                              {task.type === 'collaboration' ? <Handshake className="w-5 h-5 text-indigo-600" /> : task.type === 'prestation' ? <DollarSign className="w-5 h-5 text-green-600" /> : task.type === 'emploi' ? <Briefcase className="w-5 h-5 text-purple-600" /> : task.type === 'stage' ? <GraduationCap className="w-5 h-5 text-blue-600" /> : <Handshake className="w-5 h-5 text-indigo-600" />}
                            </span>
                            <div>
                              <span className="font-medium text-gray-800">{task.titre}</span>
                              <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded">
                                {task.type}
                              </span>
                              {task.remuneration && (
                                <span className="ml-2 text-green-600 text-sm font-medium">
                                  {Number(task.remuneration).toLocaleString()} XOF
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tasks.length === 0 && existingTasks.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <span className="text-4xl mb-2 block"><Users className="w-10 h-10 mx-auto text-gray-400" /></span>
                    <p className="text-gray-500">Aucun poste défini pour l'instant</p>
                    <p className="text-sm text-gray-400">Vous pouvez en ajouter plus tard</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> {error}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex gap-4">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition"
              >
                ← Retour
              </button>
            )}
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={(e) => nextStep(e)}
                className="flex-1 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition"
              >
                Suivant →
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || uploadingImage}
                className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading || uploadingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {uploadingImage ? 'Upload image...' : (isEditMode ? 'Mise à jour...' : 'Création...')}
                  </>
                ) : (
                  <>
                    {isEditMode ? <><Check className="w-5 h-5" /> Enregistrer les modifications</> : <><Rocket className="w-5 h-5" /> Créer le projet</>}
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProject;
